import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, RotateCcw, Trash2 } from "lucide-react";
import { SERVICE_STAGE_LABELS } from "../../lib/serviceConstants";
import { Button } from "../ui/button";
import { FlowpiAlertDialog } from "../ui/flowpi-dialog";
import { FieldFeedback, FormError } from "../ui/feedback";

export default function ServiceLifecycleDialog({
  mode,
  open,
  ticket,
  error = "",
  onClose,
  onConfirm,
}) {
  const [reason, setReason] = useState("");
  const [localError, setLocalError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const requestInFlightRef = useRef(false);
  const isDelete = mode === "delete";

  useEffect(() => {
    if (!open) return;
    setReason("");
    setLocalError("");
    setSubmitting(false);
    requestInFlightRef.current = false;
  }, [mode, open, ticket?.id]);

  if (!open || !ticket) return null;

  const close = () => {
    if (requestInFlightRef.current) return;
    onClose?.();
  };

  const submit = async () => {
    if (requestInFlightRef.current) return;
    const normalizedReason = reason.trim();
    if (isDelete && !normalizedReason) {
      setLocalError("Delete Reason is required.");
      return;
    }

    requestInFlightRef.current = true;
    setSubmitting(true);
    setLocalError("");
    try {
      await onConfirm?.(isDelete ? normalizedReason : normalizedReason || null);
    } catch {
      // The parent owns the normalized API error displayed by this dialog.
    } finally {
      requestInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const title = isDelete ? "Delete Service Ticket" : "Restore Service Ticket";
  const actionLabel = isDelete ? "Delete" : "Restore";
  const pendingLabel = isDelete ? "Deleting..." : "Restoring...";
  const stageLabel = SERVICE_STAGE_LABELS[ticket.stage] || ticket.stage || "-";

  const footer = (
    <>
      <Button type="button" onClick={close} disabled={submitting} variant="cancel" data-action-category="cancel">
        Cancel
      </Button>
      <Button
        type="button"
        onClick={submit}
        disabled={submitting}
        data-testid={`service-${mode}-confirm`}
        variant={isDelete ? "destructive" : "restore"}
        data-action-category={isDelete ? "destructive" : "restore-recovery"}
      >
        {isDelete ? <Trash2 className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
        {submitting ? pendingLabel : actionLabel}
      </Button>
    </>
  );

  return (
    <FlowpiAlertDialog
      open={open}
      onClose={close}
      busy={submitting}
      eyebrow="Service Lifecycle"
      title={title}
      closeLabel={`Close ${title}`}
      footer={footer}
      testId={`service-${mode}-modal`}
    >
      <div className="space-y-5">
          <div className={`flex gap-3 rounded-sm border px-4 py-3 text-sm ${isDelete ? "border-destructive/30 bg-destructive/5 text-destructive" : "border-primary/30 bg-primary/5 text-zinc-700 dark:text-zinc-200"}`}>
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
            <p>
              {isDelete
                ? "This ticket will leave all normal Stage lists. Its lifecycle and history data will be preserved."
                : `This ticket will return to the active ${stageLabel} Stage list.`}
            </p>
          </div>

          <dl className="grid gap-3 rounded-sm border border-zinc-200 bg-zinc-50 px-4 py-4 text-sm dark:border-zinc-800 dark:bg-zinc-900/50 sm:grid-cols-2">
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Ticket Number</dt>
              <dd className="mt-1 font-semibold tracking-[0.02em] text-zinc-900 dark:text-zinc-100">{ticket.ticket_number || "-"}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Customer</dt>
              <dd className="mt-1 text-zinc-900 dark:text-zinc-100">{ticket.owner?.name || "-"}</dd>
            </div>
            {isDelete ? (
              <div>
                <dt className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Serial Number</dt>
                <dd className="mt-1 font-medium tracking-[0.02em] text-zinc-900 dark:text-zinc-100">{ticket.device?.serial_number || "-"}</dd>
              </div>
            ) : null}
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">{isDelete ? "Current Stage" : "Return Stage"}</dt>
              <dd className="mt-1 text-zinc-900 dark:text-zinc-100">{stageLabel}</dd>
            </div>
          </dl>

          <div>
            <label htmlFor={`service-${mode}-reason`} className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-600 dark:text-zinc-300">
              {isDelete ? "Delete Reason" : "Restore Reason (Optional)"}
            </label>
            <textarea
              id={`service-${mode}-reason`}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                setLocalError("");
              }}
              disabled={submitting}
              aria-invalid={Boolean(localError)}
              aria-describedby={localError ? `service-${mode}-reason-error` : undefined}
              className="mt-2 min-h-[100px] w-full resize-none rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
              placeholder={isDelete ? "Explain why this ticket is being deleted" : "Optional restoration context"}
            />
            <FieldFeedback id={`service-${mode}-reason-error`} data-testid={localError ? `service-${mode}-error` : undefined}>
              {localError}
            </FieldFeedback>
          </div>
          <FormError data-testid={error ? `service-${mode}-error` : undefined}>{error}</FormError>
      </div>
    </FlowpiAlertDialog>
  );
}
